import { clientApi } from "@api";
import { enumFilter, enumJoinedProgram, initPage } from "@configs";
import { IClient, IClientSearch, IPagination } from "@interfaces";
import { setLoading } from "@redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { getApiErrorMessage } from "../apiError";
import { useNotify } from "../notifyHook";

export type ClientDetailVariant = "standard" | "cdic";

export const clientQueryKeys = {
  all: ["clients"] as const,
  lists: () => [...clientQueryKeys.all, "list"] as const,
  list: (params: IClientSearch) => [...clientQueryKeys.lists(), params] as const,
  details: () => [...clientQueryKeys.all, "detail"] as const,
  detail: (variant: ClientDetailVariant, id: string) =>
    [...clientQueryKeys.details(), variant, id] as const,
};

const fetchClients = async (params: IClientSearch) => {
  const res = await clientApi.clients(params);
  return {
    data: res.data?.data as IClient[],
    total: res.data?.nTotal as number,
  };
};

export const useClientDetail = (id: string | undefined, variant: ClientDetailVariant) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { error } = useNotify();

  const query = useQuery({
    queryKey: clientQueryKeys.detail(variant, id ?? ""),
    queryFn: async () => {
      const res =
        variant === "cdic"
          ? await clientApi.cdicClient(id as string)
          : await clientApi.client(id as string);
      return res.data as IClient;
    },
    enabled: Boolean(id),
  });

  useEffect(() => {
    dispatch(setLoading(query.isFetching));
  }, [dispatch, query.isFetching]);

  useEffect(() => {
    if (query.isError) {
      error(getApiErrorMessage(query.error, t("message.fetch.fail")));
    }
  }, [query.isError, query.error, error, t]);

  return query;
};

export const useClientListPage = (program: enumJoinedProgram.MNCH | enumJoinedProgram.CDIC) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { error } = useNotify();

  const [page, setPage] = useState<IPagination>(initPage);
  const [filters, setFilters] = useState<enumFilter | string>("");
  const [text, setText] = useState("");
  const [date, setDate] = useState<string[]>([]);

  const params: IClientSearch = {
    page: page.page,
    limit: page.limit,
    searchColumn: filters,
    search: text,
    startDate: date[0],
    endDate: date[1],
    program,
  };

  const query = useQuery({
    queryKey: clientQueryKeys.list(params),
    queryFn: () => fetchClients(params),
    placeholderData: (previousData) => previousData,
  });

  useEffect(() => {
    dispatch(setLoading(query.isFetching));
  }, [dispatch, query.isFetching]);

  useEffect(() => {
    if (query.isError) {
      error(getApiErrorMessage(query.error, t("message.fetch.fail")));
    }
  }, [query.isError, query.error, error, t]);

  const handlePagination = (current: number, size: number) => {
    setPage({ ...page, page: current, limit: size });
  };

  const changeFilter = (value: string) => setFilters(value);
  const searchText = (value: string) => setText(value);

  const handleClearSearch = () => {
    setText("");
    setFilters("");
  };

  const handleClearDate = () => setDate([]);

  return {
    data: query.data?.data ?? [],
    total: query.data?.total ?? 0,
    page,
    filters,
    text,
    date,
    refetch: query.refetch,
    handlePagination,
    changeFilter,
    searchText,
    handleClearSearch,
    handleClearDate,
  };
};

const invalidateClientLists = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: clientQueryKeys.lists() });
};

export const useCreateClient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: IClient) => clientApi.create(payload),
    onSuccess: () => invalidateClientLists(queryClient),
  });
};

export const useUpdateClient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ payload, id }: { payload: IClient; id: string }) =>
      clientApi.update(payload, id),
    onSuccess: (_data, { id }) => {
      invalidateClientLists(queryClient);
      queryClient.invalidateQueries({ queryKey: clientQueryKeys.details() });
      queryClient.invalidateQueries({ queryKey: clientQueryKeys.detail("standard", id) });
      queryClient.invalidateQueries({ queryKey: clientQueryKeys.detail("cdic", id) });
    },
  });
};

export const useUpdateClientReferredBy = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: { referredBy: string; clientId: string }) =>
      clientApi.updateReferredBy(params),
    onSuccess: () => invalidateClientLists(queryClient),
  });
};

export const useUpdateClientDeliverySite = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: { deliverySite: string; clientId: string }) =>
      clientApi.updateDeliverySite(params),
    onSuccess: () => invalidateClientLists(queryClient),
  });
};

export const useExportClientRecords = () => {
  const dispatch = useDispatch();

  const mutation = useMutation({
    mutationFn: ({
      variant,
      params,
    }: {
      variant: ClientDetailVariant;
      params: IClientSearch;
    }) => (variant === "cdic" ? clientApi.exportCdic(params) : clientApi.export(params)),
  });

  useEffect(() => {
    dispatch(setLoading(mutation.isPending));
  }, [dispatch, mutation.isPending]);

  return mutation;
};
